import {
    MapCheckIn,
    MapWorkout,
    BoundingBox,
    DEFAULT_VIEW_STATE,
    ViewState
} from './GpxMap.types';
import { parseGPX } from '@we-gold/gpxjs';

export const fetchAndParseGpx = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch GPX from ${url}`);
    }

    const isGzipped =
        url.includes('.gz') ||
        response.headers.get('content-encoding') === 'gzip' ||
        response.headers.get('content-type')?.includes('gzip');

    if (isGzipped && response.body) {
        const ds = new DecompressionStream('gzip');
        return await new Response(response.body.pipeThrough(ds)).text();
    }

    return await response.text();
};

export function parseGpxCoordinates(
    gpxContent: string
): Array<[number, number]> {
    try {
        const [parsedGpx, error] = parseGPX(gpxContent);

        if (error) {
            console.error('Error parsing GPX file:', error);
            return [];
        }

        if (!parsedGpx || !parsedGpx.tracks || parsedGpx.tracks.length === 0) {
            return [];
        }

        const coordinates: Array<[number, number]> = [];

        parsedGpx.tracks.forEach((track) => {
            track.points.forEach((point) => {
                if (point.latitude !== null && point.longitude !== null) {
                    coordinates.push([point.longitude, point.latitude]);
                }
            });
        });

        return coordinates;
    } catch (e) {
        console.error('Unexpected error parsing GPX:', e);
        return [];
    }
}

export const calculateInitialViewState = (
    parsedWorkouts: MapWorkout[],
    filteredCheckIns: MapCheckIn[]
): ViewState => {
    const allPoints: Array<[number, number]> = [];

    parsedWorkouts.forEach((workout) => {
        allPoints.push(...workout.positions);
    });

    filteredCheckIns.forEach((checkIn) => {
        if (checkIn.location) allPoints.push(checkIn.location);
    });

    const bbox = calculateBoundingBox(allPoints);

    if (!bbox) {
        return DEFAULT_VIEW_STATE;
    }

    const center = calculateCenter(bbox);

    const zoom = calculateZoomLevel({
        boundingBox: bbox,
        containerWidth: 800,
        containerHeight: 400
    });

    return {
        longitude: center[0],
        latitude: center[1],
        zoom: zoom
    };
};

const _maxZoom = 18;
const _minZoom = 1;
const _paddingPercent = 0.1;

export const calculateBoundingBox = (
    points: Array<[number, number]>
): BoundingBox | null => {
    if (points.length === 0) return null;

    let minLng = points[0][0];
    let maxLng = points[0][0];
    let minLat = points[0][1];
    let maxLat = points[0][1];

    for (const [lng, lat] of points) {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    }

    return {
        southWest: [minLng, minLat],
        northEast: [maxLng, maxLat]
    };
};

export const calculateCenter = (boundingBox: BoundingBox): [number, number] => {
    return [
        (boundingBox.southWest[0] + boundingBox.northEast[0]) / 2,
        (boundingBox.southWest[1] + boundingBox.northEast[1]) / 2
    ];
};

export const calculateZoomLevel = ({
    boundingBox,
    containerWidth: _containerWidth,
    containerHeight: _containerHeight,
    zoomOffset
}: {
    boundingBox: BoundingBox;
    containerWidth: number;
    containerHeight: number;
    zoomOffset?: number;
}): number => {
    const latSpan = boundingBox.northEast[1] - boundingBox.southWest[1];
    const lngSpan = boundingBox.northEast[0] - boundingBox.southWest[0];

    const paddedLatSpan = latSpan * (1 + _paddingPercent);
    const paddedLngSpan = lngSpan * (1 + _paddingPercent);

    let zoom = _maxZoom;

    if (paddedLatSpan > 0) {
        const latZoom = Math.log(180 / paddedLatSpan) / Math.LN2;
        zoom = Math.min(zoom, latZoom);
    }

    if (paddedLngSpan > 0) {
        const lngZoom = Math.log(360 / paddedLngSpan) / Math.LN2;
        zoom = Math.min(zoom, lngZoom);
    }

    zoom = zoom - (zoomOffset ?? 0);

    return Math.max(_minZoom, Math.min(_maxZoom, zoom));
};

const CONTRAST_PALETTE = [
    '#FF595E', // Red-Orange
    '#FFCA3A', // Saffron Yellow
    '#8AC926', // Lime Green
    '#1982C4', // Steel Blue
    '#6A4C93', // Royal Purple
    '#F15BB5', // Hot Pink
    '#00BBF9', // Deep Sky Blue
    '#00F5D4', // Turquoise
    '#FB5607', // Deep Orange
    '#9B5DE5', // Medium Purple
    '#3A86FF', // Neon Blue
    '#F72585', // Vivid Raspberry
    '#4CC9F0', // Sky Cyan
    '#7209B7', // Grape
    '#2EC4B6', // Light Sea Green
    '#E71D36', // Alizarin Crimson
    '#FF9F1C', // Orange Peel
    '#4361EE', // Palatinate Blue
    '#FF006E', // Winter Sky
    '#FEE440' // Sunglow
];

const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

export const getColorForId = (id: string): string =>
    CONTRAST_PALETTE[hashString(id) % CONTRAST_PALETTE.length];
